import React, { useCallback } from "react";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { ReactComponent as InstagramIcon } from "./assets/instagram.svg";
import { ReactComponent as InstagramBorderedIcon } from "./assets/instagram-2.svg";

import { CRMAccountCard } from "../CRMAccountCard";
import { CRMAccountTile } from "../CRMAccountTile";
import { ManageAccountsDrawerScreenToParametersMap } from "../../types";
import { ReactComponent as RemoveIcon } from "./assets/remove.svg";
import { useInstagramAccount } from "../../../../providers/InstagramAccountProvider/useInstagramAccount";
import defaultAvatar from "./assets/default-avatar.png";
import { useManageAccountsDrawer } from "../../providers/ManageAccountsDrawerContext";

export const InstagramAccountCard: React.FC<{
  hideReconnectOption?: boolean;
  isClickable?: boolean;
  variant?: "card" | "tile";
}> = ({ hideReconnectOption, isClickable, variant = "card" }) => {
  const { t } = useTranslations();
  const { integrationsApi } = useRestApiProvider();
  const { instagramAccount, isConnected, loginInstagramAccount } =
    useInstagramAccount();
  const { openManageAccountDrawer, isInboxModule } = useManageAccountsDrawer();
  const { navigateToPage } = useNavigation();
  const { toast } = useToast();
  const { entityId } = useEntitySelectedProvider();
  const { transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();

  const [disconnectInstagramAccount, { isLoading }] =
    integrationsApi.useDisconnectInstagramAccountMutation();

  const handleRemoveAccount = useCallback(async () => {
    const resp = await disconnectInstagramAccount({ entityId });

    if (resp && "data" in resp) {
      openManageAccountDrawer(undefined);
      if (isInboxModule) {
        navigateToPage("clientSupport");
      }
      return;
    }

    toast(
      t({
        id: "feedback.something-went-wrong",
        defaultMessage: "Something went wrong",
      }),
      { variant: "error" }
    );
  }, [
    disconnectInstagramAccount,
    entityId,
    isInboxModule,
    navigateToPage,
    openManageAccountDrawer,
    t,
    toast,
  ]);

  if (variant === "tile") {
    const handleClick = () => {
      if (!isClickable) {
        return;
      }
      if (!isConnected) {
        loginInstagramAccount();
      } else {
        transitionTo({ name: "instagramAccount" });
      }
    };
    return (
      <CRMAccountTile
        isConnected={isConnected}
        title={
          (instagramAccount?.username || instagramAccount?.business_id) ??
          t({
            id: "client-support-page.instagram",
            defaultMessage: "Instagram",
          })
        }
        onClick={handleClick}
        img={instagramAccount?.profile_picture_url}
        defaultAvatar={defaultAvatar}
        icon={InstagramBorderedIcon}
        description={
          isConnected
            ? `${instagramAccount?.name}  •  ID: ${instagramAccount?.business_id}`
            : t({
                id: "client-support-page.instagram-account-disconnected-2",
                defaultMessage:
                  "Elevate your marketing with Instagram CRM. Connect your account and let automation handle the rest 🚀",
              })
        }
        status={isConnected ? "accountConnected" : "accountDisconnected"}
        menuItems={[
          [
            {
              id: "reconnect-account",
              label: t({
                id: "client-support-page.reconnect",
                defaultMessage: "Reconnect",
              }),
              onSelect: loginInstagramAccount,
              isDisabled: isLoading,
              isHidden: hideReconnectOption || isConnected,
            },
            {
              id: "disconnect-account",
              label: t({
                id: "client-support-page.disconnect-account",
                defaultMessage: "Disconnect Account",
              }),
              icon: <RemoveIcon />,
              onSelect: handleRemoveAccount,
              isDisabled: isLoading,
            },
          ],
        ]}
      />
    );
  }

  if (!instagramAccount) return null;

  return (
    <CRMAccountCard
      img={instagramAccount.profile_picture_url}
      title={instagramAccount.username || instagramAccount.business_id}
      buttonProps={
        !hideReconnectOption && !isConnected
          ? {
              text: t({
                id: "client-support-page.reconnect",
                defaultMessage: "Reconnect",
              }),
              onClick: loginInstagramAccount,
            }
          : undefined
      }
      onClick={
        isClickable
          ? () => transitionTo({ name: "instagramAccount" })
          : undefined
      }
      icon={InstagramIcon}
      description={
        isConnected
          ? `${instagramAccount.name}  •  ID: ${instagramAccount.business_id}`
          : t({
              id: "client-support-page.instagram-account-disconnected",
              defaultMessage:
                "Instagram Account is disconnected. Please reconnect to continue using Instagram CRM",
            })
      }
      status={isConnected ? "accountConnected" : "accountDisconnected"}
      menuItems={[
        [
          {
            id: "disconnect-account",
            label: t({
              id: "client-support-page.disconnect-account",
              defaultMessage: "Disconnect Account",
            }),
            icon: <RemoveIcon />,
            onSelect: handleRemoveAccount,
            isDisabled: isLoading,
          },
        ],
      ]}
    />
  );
};
